import { TOOLKIT_VERSION, type GameModule, type Toolkit } from '../sdk/toolkit';

const THEME_VARS = ['--bg', '--surface', '--surface-soft', '--ink', '--ink-soft', '--primary', '--primary-ink', '--border',
  '--border-strong', '--success', '--success-bg', '--focus', '--radius', '--radius-lg', '--tile-radius', '--font-size-base', '--line'];
const MAX_ROUNDS_PER_MINUTE = 120;
const MAX_PRESENTED = 4000;
const MAX_ANSWER = 1000;
const MAX_RESPONSE = 200;
const MAX_SPEECH = 500;

export type SealedSpec = {
  code: string;
  title: string;
  config: Record<string, unknown>;
  tag?: Record<string, unknown>;
  empty?: string | null;
};

function themeOf(): Record<string, string> {
  const cs = getComputedStyle(document.documentElement);
  const out: Record<string, string> = {};
  for (const v of THEME_VARS) {
    const val = cs.getPropertyValue(v).trim();
    if (val) out[v] = val;
  }
  out['--font'] = getComputedStyle(document.body).fontFamily;
  return out;
}

function emptyCard(el: HTMLElement, kit: Toolkit, text: string) {
  const card = document.createElement('div');
  card.className = 'card';
  const p1 = document.createElement('p');
  p1.textContent = kit.t('This program has nothing to practise yet.');
  const p2 = document.createElement('p');
  p2.className = 'field-hint';
  p2.textContent = text;
  card.append(p1, p2);
  el.appendChild(card);
  kit.session.status('empty');
  return { unmount() { card.remove(); } };
}

export function sealedModule(spec: SealedSpec): GameModule {
  return {
    toolkit: TOOLKIT_VERSION,
    mount(el, kit) {
      if (spec.empty) return emptyCard(el, kit, spec.empty);
      const board = document.createElement('div');
      board.className = 'g-board g-studio';
      el.appendChild(board);
      let frame: HTMLIFrameElement | null = null;
      const issued = new Set<string>();
      const stamps: number[] = [];

      const post = (msg: Record<string, unknown>) => frame?.contentWindow?.postMessage(msg, '*');
      const reply = (reqId: unknown, value: unknown, error?: string) => post({ type: 'result', reqId, value, error });

      function load() {
        frame?.remove();
        issued.clear();
        frame = document.createElement('iframe');
        frame.className = 'g-studio-frame';
        frame.title = spec.title;
        frame.src = '/studio/runtime.html';
        frame.setAttribute('sandbox', 'allow-scripts');
        frame.referrerPolicy = 'no-referrer';
        frame.style.height = '360px';
        board.appendChild(frame);
      }

      const onMessage = async (e: MessageEvent) => {
        if (!frame || e.source !== frame.contentWindow) return;
        const m = e.data as Record<string, unknown> | null;
        if (!m || typeof m !== 'object' || typeof m.type !== 'string') return;
        if (m.type === 'ready') {
          kit.session.status('playing');
          post({
            type: 'init',
            code: spec.code,
            config: spec.config,
            settings: {
              reduceMotion: kit.prefs.reduceMotion,
              errorless: kit.content.errorless === 'on',
              textPrompt: kit.content.promptMode === 'TEXT',
              transitionMs: kit.prefs.transitionMs,
            },
            theme: themeOf(),
          });
        } else if (m.type === 'register') {
          const now = Date.now();
          while (stamps.length && now - stamps[0] > 60_000) stamps.shift();
          if (stamps.length >= MAX_ROUNDS_PER_MINUTE) { reply(m.reqId, null, 'too many rounds'); return; }
          stamps.push(now);
          const presented = m.presented && typeof m.presented === 'object' ? m.presented as Record<string, unknown> : {};
          const answer = m.answer && typeof m.answer === 'object' ? m.answer as Record<string, unknown> : null;
          if (JSON.stringify(presented).length > MAX_PRESENTED || JSON.stringify(answer ?? {}).length > MAX_ANSWER) {
            reply(m.reqId, null, 'round too large');
            return;
          }
          const id = await kit.trials.register({ ...presented, ...spec.tag }, answer);
          if (id) { issued.add(id); kit.session.current(id); }
          reply(m.reqId, id, id ? undefined : 'could not start the round');
        } else if (m.type === 'respond') {
          if (typeof m.trialId !== 'string' || !issued.has(m.trialId)) { reply(m.reqId, null, 'unknown round'); return; }
          issued.delete(m.trialId);
          const v = await kit.trials.respond(m.trialId, String(m.response ?? '').slice(0, MAX_RESPONSE));
          reply(m.reqId, v ? { correct: v.correct } : null, v ? undefined : 'could not record the answer');
        } else if (m.type === 'rest') {
          if (typeof m.trialId === 'string' && issued.has(m.trialId)) {
            kit.session.current(m.trialId);
            kit.session.takeBreak();
          }
          reply(m.reqId, true);
        } else if (m.type === 'say') {
          if (typeof m.text === 'string') kit.speech.say(m.text.slice(0, MAX_SPEECH));
        } else if (m.type === 'resize') {
          const h = Number(m.height);
          if (Number.isFinite(h) && frame) frame.style.height = `${Math.max(120, Math.min(4000, Math.round(h)))}px`;
        } else if (m.type === 'error') {
          kit.session.fail(kit.t('This game stopped working. Try reloading the page.'));
        }
      };

      window.addEventListener('message', onMessage);
      const offResume = kit.session.onResume(load);
      const offPause = kit.clock.onPauseChange((paused) => post({ type: 'pause', paused }));
      load();
      return {
        unmount() {
          window.removeEventListener('message', onMessage);
          offResume();
          offPause();
          board.remove();
        },
      };
    },
  };
}
